/**
 * cookies.js
 *
 * Utility functions for persisting application
 * data to cookie storage. All cookies are scoped to
 * the host APP_HOSTNAME and path '/'
 *
 */

import { APP_HOSTNAME } from '../constants';

type CookieOptions = {
	expires?: string;
	'max-age'?: number;
	domain?: string;
};

export type CookieValue = string | null | undefined;

export const setCookie = (
	name: string,
	value: CookieValue,
	options?: CookieOptions,
): CookieValue => {
	const expires =
		(options && options.expires) ||
		(options && options['max-age'] && getExpires(options['max-age'])) ||
		getExpires(60 * 30);

	// Use provided domain option if specified, otherwise fall back to APP_HOSTNAME
	// Domain should include leading dot for subdomain sharing (e.g., '.stash.com')
	let domain = `.${APP_HOSTNAME}`;
	if (options && options.domain) {
		domain = options.domain.startsWith('.') ? options.domain : `.${options.domain}`;
	}

	const secureCookie = `${name}=${value}; expires=${expires}; secure=true; domain=${domain}; path=/`;
	const testCookie = `${name}=${value}; expires=${expires}; path=/`;
	// Set the cookie.
	document.cookie = process.env.NODE_ENV === 'test' ? testCookie : secureCookie;

	return value;
};

export const getCookie = (name: string): CookieValue => {
	const escape = (s: string): string => {
		// eslint-disable-next-line no-useless-escape
		return s.replace(/([.*+?\^${}()|\[\]\/\\])/g, '\\$1');
	};
	const match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
	if (!!match) {
		if (match[1] === 'null') {
			return null;
		}
		if (match[1] === 'undefined') {
			return undefined;
		}
		return match[1];
	}

	return undefined;
};

export const deleteCookie = (name: string): CookieValue => {
	setCookie(name, '', { 'max-age': -1, domain: 'stash.com' });
	return setCookie(name, '', { 'max-age': -1 });
};

const getExpires = (maxAge: number): string => {
	// convert a maxAge to an 'expires' date.
	return new Date(Date.now() + maxAge * 1000).toUTCString();
};
